
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {CommissionProfileDeleteResponse} from './CommissionProfileDeleteResponse';

/**
 * CommissionProfileDeleteRequest struct
 */
class CommissionProfileDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} commissionProfileId
   * @param {string=} commissionProfileName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), commissionProfileId = null, commissionProfileName = null) {
    super()
    super.copy(parent)
    this.CommissionProfileId = commissionProfileId
    this.CommissionProfileName = commissionProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionProfileDeleteRequest}
   * @param {!CommissionProfileDeleteRequest} other Other struct
   * @returns {!CommissionProfileDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CommissionProfileId != null) {
      this.CommissionProfileId = new UUID(other.CommissionProfileId)
    } else {
      this.CommissionProfileId = null
    }
    if (other.CommissionProfileName != null) {
      this.CommissionProfileName = other.CommissionProfileName
    } else {
      this.CommissionProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionProfileDeleteRequest}
   * @returns {!CommissionProfileDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionProfileDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionProfileDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionProfileDeleteRequest}
   * @param {!CommissionProfileDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionProfileDeleteRequest)) {
      throw new TypeError('Instance of CommissionProfileDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionProfileDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CommissionProfileId: ((this.CommissionProfileId != null) ? this.CommissionProfileId.toString() : null),
      CommissionProfileName: ((this.CommissionProfileName != null) ? this.CommissionProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionProfileDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionProfileDeleteRequest} other Object value
   * @returns {!CommissionProfileDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new CommissionProfileDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionProfileDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6130
  }
}

export { CommissionProfileDeleteRequest };

/**
 * Fast Binary Encoding CommissionProfileDeleteRequest field model
 */
class FieldModelCommissionProfileDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.CommissionProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.CommissionProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.CommissionProfileId.fbeOffset + this.CommissionProfileId.fbeSize), buffer, this.CommissionProfileId.fbeOffset + this.CommissionProfileId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CommissionProfileId.fbeSize + this.CommissionProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CommissionProfileId.fbeExtra + this.CommissionProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionProfileDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6130
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileId.fbeSize

    if ((fbeCurrentSize + this.CommissionProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!CommissionProfileDeleteRequest} fbeValue Default value, defaults is new CommissionProfileDeleteRequest()
   * @returns {!CommissionProfileDeleteRequest} CommissionProfileDeleteRequest value
   */
  get (fbeValue = new CommissionProfileDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!CommissionProfileDeleteRequest} fbeValue CommissionProfileDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfileId = this.CommissionProfileId.get()
    } else {
      fbeValue.CommissionProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileId.fbeSize

    if ((fbeCurrentSize + this.CommissionProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfileName = this.CommissionProfileName.get()
    } else {
      fbeValue.CommissionProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {!CommissionProfileDeleteRequest} fbeValue CommissionProfileDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionProfileDeleteRequest}
   * @param {CommissionProfileDeleteRequest} fbeValue CommissionProfileDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CommissionProfileId.set(fbeValue.CommissionProfileId)
    this.CommissionProfileName.set(fbeValue.CommissionProfileName)
  }
}

export { FieldModelCommissionProfileDeleteRequest };

/**
 * Fast Binary Encoding CommissionProfileDeleteRequest model
 */
class CommissionProfileDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionProfileDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the CommissionProfileDeleteRequest model
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!FieldModelCommissionProfileDeleteRequest} model CommissionProfileDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionProfileDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionProfileDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionProfileDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionProfileDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionProfileDeleteRequestModel}
   * @param {!CommissionProfileDeleteRequest} value CommissionProfileDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionProfileDeleteRequestModel}
   * @param {!CommissionProfileDeleteRequest} value CommissionProfileDeleteRequest value, defaults is new CommissionProfileDeleteRequest()
   * @return {!object} Deserialized CommissionProfileDeleteRequest value and its size
   */
  deserialize (value = new CommissionProfileDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionProfileDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionProfileDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionProfileDeleteRequestModel}
   * @param {!number} prev Previous CommissionProfileDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionProfileDeleteRequestModel };
CommissionProfileDeleteRequest.__has_response = true;
CommissionProfileDeleteRequest.__response_class = CommissionProfileDeleteResponse;
